import { MIN_HEIGHT_CHANGE_M, ON_TRAY_TOLERANCE_M } from "./constants";
import { trayTypeAlongPath } from "./trayTypeAlongPath";
import type { RouteWaypoint } from "../../../ReviewPage/hooks/useCircuitCablePaths/types";
import type { InstallationHeights, InstallationLevel } from "../installationHeights";
import type { BuildDeviceHeightProfile, HeightLeg, HeightLegReason } from "./types";

const LEVEL_KEY: Record<InstallationLevel, keyof InstallationHeights> = {
  floor: "floorM",
  controls: "controlsM",
  luminaire: "luminaireM",
};

const verticalLeg = (
  fromM: number,
  toM: number,
  at: RouteWaypoint,
  reason: HeightLegReason,
  trayType: string,
  level?: InstallationLevel,
): HeightLeg[] =>
  Math.abs(toM - fromM) < MIN_HEIGHT_CHANGE_M
    ? []
    : [
        {
          kind: toM > fromM ? "rise" : "drop",
          reason,
          fromM,
          toM,
          lengthM: Math.abs(toM - fromM),
          at,
          trayType,
          ...(level ? { level } : {}),
        },
      ];

export const buildDeviceHeightProfile: BuildDeviceHeightProfile = ({
  destinationId,
  waypoints,
  trays,
  boardLabel,
  level,
  heights,
  metresPerPoint,
  stops = [],
  endM,
}) => {
  const boardM = heights.boardM(boardLabel);
  const deviceM = endM ?? heights.levels[LEVEL_KEY[level]];
  const endReason: HeightLegReason = endM === undefined ? "device" : "boardEnd";
  const start = waypoints[0] ?? { x: 0, y: 0 };
  const end = waypoints[waypoints.length - 1] ?? start;
  const ridden = trayTypeAlongPath({
    waypoints,
    trays,
    tolerance: ON_TRAY_TOLERANCE_M / (metresPerPoint || 1),
  });
  const lengths = ridden.map((_, index) => {
    const from = waypoints[index];
    const to = waypoints[index + 1];
    return Math.hypot(to.x - from.x, to.y - from.y) * metresPerPoint;
  });
  const horizontalM = lengths.reduce((sum, metres) => sum + metres, 0);

  if (horizontalM < MIN_HEIGHT_CHANGE_M) {
    const legs = verticalLeg(boardM, deviceM, end, endReason, "");
    return { destinationId, boardLabel, boardM, level, deviceM, legs, verticalM: legs[0]?.lengthM ?? 0, horizontalM: 0 };
  }

  // Off a tray the cable keeps the height it was at; before the first tray it is already at that tray's.
  const firstTray = ridden.find((type): type is string => type !== null);
  let currentM = firstTray !== undefined ? heights.trayM(firstTray) : heights.levels.ceilingVoidM;
  const legs: HeightLeg[] = [...verticalLeg(boardM, currentM, start, "board", firstTray ?? "")];

  const stopAt = new Map(stops.map((stop) => [stop.index, stop.level]));
  ridden.forEach((trayType, index) => {
    const at = waypoints[index];
    const passed = stopAt.get(index);
    if (passed) {
      const passedM = heights.levels[LEVEL_KEY[passed]];
      legs.push(...verticalLeg(currentM, passedM, at, "passDevice", "", passed));
      currentM = passedM;
      // From a light to the next the cable stays at the lights; from a switch it climbs back to the Zwischendecke.
      if (trayType === null && passed !== "luminaire") {
        legs.push(...verticalLeg(currentM, heights.levels.ceilingVoidM, at, "leaveDevice", ""));
        currentM = heights.levels.ceilingVoidM;
      }
    }
    if (trayType !== null) {
      const trayM = heights.trayM(trayType);
      legs.push(...verticalLeg(currentM, trayM, at, "trayChange", trayType));
      currentM = trayM;
    }
    const reason: HeightLegReason = trayType !== null ? "tray" : "freeRun";
    const last = legs[legs.length - 1];
    if (last?.kind === "run" && last.reason === reason && last.trayType === (trayType ?? "")) {
      last.lengthM += lengths[index];
      return;
    }
    legs.push({ kind: "run", reason, fromM: currentM, toM: currentM, lengthM: lengths[index], at, trayType: trayType ?? "" });
  });

  legs.push(...verticalLeg(currentM, deviceM, end, endReason, ""));
  const verticalM = legs.reduce((sum, leg) => (leg.kind === "run" ? sum : sum + leg.lengthM), 0);
  return { destinationId, boardLabel, boardM, level, deviceM, legs, verticalM, horizontalM };
};
